import { useEffect, useState } from "react";
import axios from "../../api/axios";
import SidebarClient from "../../components/SidebarClient";

export default function Contacts() {
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [showAdd, setShowAdd] = useState(false);

  const [newContact, setNewContact] = useState({
    name: "",
    phone: "",
    tags: "",
  });

  // Auto-Save Rules State
  const [rules, setRules] = useState([]);
  const [loadingRules, setLoadingRules] = useState(true);
  const [showAddRule, setShowAddRule] = useState(false);
  const [newRule, setNewRule] = useState({
    trigger_phrase: "",
    target_tag: "",
  });

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/contacts");
      const list = Array.isArray(res.data) ? res.data : res.data?.contacts || [];
      const normalized = list.map((c) => ({
        id: c.id,
        name: c.name,
        phone: c.phone_number || c.phone || "",
        tags: Array.isArray(c.tags) ? c.tags.join(", ") : c.tags || "",
        email: c.email || "",
      }));
      setContacts(normalized);
    } catch (err) {
      alert(err?.response?.data?.error || err?.response?.data?.message || "Failed to load contacts");
    }
    setLoading(false);
  };

  const fetchRules = async () => {
    setLoadingRules(true);
    try {
      const res = await axios.get("/contacts/auto-save-rules");
      setRules(res.data?.rules || []);
    } catch (err) {
      console.error("Failed to fetch rules:", err);
    }
    setLoadingRules(false);
  };

  useEffect(() => {
    fetchContacts();
    fetchRules();
  }, []);

  const addRule = async (e) => {
    e.preventDefault();
    try {
      await axios.post("/contacts/auto-save-rules", {
        trigger_phrase: newRule.trigger_phrase,
        target_tag: newRule.target_tag,
      });
      setShowAddRule(false);
      setNewRule({ trigger_phrase: "", target_tag: "" });
      fetchRules();
    } catch (err) {
      alert(err?.response?.data?.error || "Failed to add rule");
    }
  };

  const deleteRule = async (id) => {
    if (!window.confirm("Delete this auto-save rule?")) return;
    try {
      await axios.delete(`/contacts/auto-save-rules/${id}`);
      fetchRules();
    } catch (err) {
      alert(err?.response?.data?.error || "Failed to delete rule");
    }
  };

  const addContact = async (e) => {
    e.preventDefault();
    try {
      const tagsArray = String(newContact.tags || "")
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);

      await axios.post("/contacts", {
        name: newContact.name,
        phone_number: newContact.phone,
        tags: tagsArray,
      });
      setShowAdd(false);
      setNewContact({ name: "", phone: "", tags: "" });
      fetchContacts();
    } catch (err) {
      alert(err?.response?.data?.error || err?.response?.data?.message || "Failed to add contact");
    }
  };

  const deleteContact = async (id) => {
    if (!window.confirm("Delete this contact?")) return;
    try {
      await axios.delete(`/contacts/${id}`);
      fetchContacts();
    } catch (err) {
      alert(err?.response?.data?.error || err?.response?.data?.message || "Failed to delete contact");
    }
  };

  const copyNumber = (phone) => {
    navigator.clipboard.writeText(String(phone));
    alert("Number copied");
  };

  return (
    <div className="flex flex-col lg:flex-row h-screen bg-gray-50">
      <SidebarClient />
      <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-y-auto mt-14 lg:mt-0">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900">Contacts</h1>
            <p className="text-gray-500 mt-1">Save contacts for bulk messaging.</p>
          </div>
          <button
            onClick={() => setShowAdd(true)}
            className="bg-blue-600 text-white px-6 py-2 rounded-xl font-bold shadow-lg hover:bg-blue-700 transition"
          >
            + Add Contact
          </button>
        </div>

        {/* --- AUTO-SAVE RULES SECTION --- */}
        <div className="mb-10 mt-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Auto-Save Rules</h2>
              <p className="text-sm text-gray-500 mt-1">Automatically save contacts when they match specific keywords.</p>
            </div>
            <button
              onClick={() => setShowAddRule(true)}
              className="bg-green-600 text-white px-4 py-2 rounded-xl font-bold hover:bg-green-700 transition shadow-sm text-sm"
            >
              + Add Rule
            </button>
          </div>

          {loadingRules ? (
            <div className="text-center py-4 text-gray-500">Loading rules...</div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden max-w-5xl">
              <table className="w-full">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="p-4 text-left font-bold">Trigger Phrase (Keyword)</th>
                    <th className="p-4 text-left font-bold">Target Tag</th>
                    <th className="p-4 text-center font-bold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rules.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="p-6 text-center text-gray-400">No automation rules defined yet.</td>
                    </tr>
                  ) : (
                    rules.map((r) => (
                      <tr key={r.id} className="hover:bg-gray-50 transition">
                        <td className="p-4 font-mono text-blue-700 font-bold">"{r.trigger_phrase}"</td>
                        <td className="p-4">
                          <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-lg text-xs font-bold border border-gray-200">{r.target_tag}</span>
                        </td>
                        <td className="p-4 text-center">
                          <button
                            onClick={() => deleteRule(r.id)}
                            className="bg-red-50 text-red-600 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-red-100 transition"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {/* --- END AUTO-SAVE RULES SECTION --- */}

        {/* --- CONTACTS LIST SECTION --- */}
        <div className="mb-6 border-b pb-2">
          <h2 className="text-xl font-bold text-gray-900">Your Contacts List</h2>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden max-w-5xl">
            <table className="w-full">
              <thead className="bg-gray-900 text-white text-xs uppercase tracking-wider">
                <tr>
                  <th className="p-4 text-left">Name</th>
                  <th className="p-4 text-left">Phone</th>
                  <th className="p-4 text-left">Tags</th>
                  <th className="p-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {contacts.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="p-8 text-center text-gray-400">No contacts yet</td>
                  </tr>
                ) : (
                  contacts.map((c) => (
                    <tr key={c.id} className="hover:bg-gray-50 transition">
                      <td className="p-4 font-bold text-gray-800">{c.name || "-"}</td>
                      <td className="p-4 font-mono text-gray-700">{c.phone || "-"}</td>
                      <td className="p-4 text-gray-600">{c.tags || "-"}</td>
                      <td className="p-4 text-center space-x-2">
                        <button
                          onClick={() => copyNumber(c.phone)}
                          className="bg-blue-100 text-blue-700 px-3 py-2 rounded-lg text-sm font-bold hover:bg-blue-200 transition"
                        >
                          Copy
                        </button>
                        <button
                          onClick={() => deleteContact(c.id)}
                          className="bg-red-100 text-red-700 px-3 py-2 rounded-lg text-sm font-bold hover:bg-red-200 transition"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {showAdd && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50 p-4">
            <div className="bg-white p-8 rounded-3xl w-full max-w-md shadow-2xl">
              <h3 className="text-2xl font-bold mb-4 text-gray-800">Add Contact</h3>
              <form onSubmit={addContact} className="space-y-4">
                <input
                  className="w-full border p-3 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="Name (optional)"
                  value={newContact.name}
                  onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                />
                <input
                  className="w-full border p-3 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="Phone (required, digits only)"
                  value={newContact.phone}
                  onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                  required
                />
                <input
                  className="w-full border p-3 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="Tags (optional, comma separated)"
                  value={newContact.tags}
                  onChange={(e) => setNewContact({ ...newContact, tags: e.target.value })}
                />

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowAdd(false)}
                    className="flex-1 bg-gray-100 py-3 rounded-xl font-bold text-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-bold shadow-lg"
                  >
                    Save
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* --- ADD RULE MODAL --- */}
        {showAddRule && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50 p-4">
            <div className="bg-white p-8 rounded-3xl w-full max-w-md shadow-2xl">
              <h3 className="text-2xl font-bold mb-4 text-gray-800">New Auto-Save Rule</h3>
              <form onSubmit={addRule} className="space-y-4">
                <div>
                  <label className="text-xs font-bold text-gray-500 ml-1">TRIGGER PHRASE</label>
                  <input
                    className="w-full border p-3 rounded-xl mt-1 focus:ring-2 focus:ring-blue-500 outline-none"
                    placeholder="e.g. you have received topup"
                    value={newRule.trigger_phrase}
                    onChange={(e) => setNewRule({ ...newRule, trigger_phrase: e.target.value })}
                    required
                  />
                  <p className="text-[10px] text-gray-400 mt-1 ml-1">If a message contains this phrase, the contact will be saved.</p>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 ml-1">TARGET TAG</label>
                  <input
                    className="w-full border p-3 rounded-xl mt-1 focus:ring-2 focus:ring-blue-500 outline-none"
                    placeholder="e.g. TOPUP_BCAST"
                    value={newRule.target_tag}
                    onChange={(e) => setNewRule({ ...newRule, target_tag: e.target.value })}
                    required
                  />
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowAddRule(false)}
                    className="flex-1 bg-gray-100 py-3 rounded-xl font-bold text-gray-600 hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-green-600 text-white py-3 rounded-xl font-bold shadow-lg hover:bg-green-700"
                  >
                    Save Rule
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
